import { atom, type PrimitiveAtom, useAtom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

import { GCodePrintData } from "_/components/view-loaders/gcode/gcode-print";

import { Uuid, Update, Tuple, Triple } from "_/types";

/*
 * Hylo Bed Dimensions in mm
 */
export const BED_DIMENSIONS = {
  width: 680,
  height: 480,
};

/**
 * View modes for the 3D viewport.
 *
 * Each view mode has an accompanying React component for rendering & settings.
 */
export type ViewMode = "prepare" | "optimize" | "analyze";

// View: the content of the 3D viewport for a specific project view mode (prepare, optimize, analyze)
// Adding the conditional with `never` ensures that TS will discriminate the union.
export type View<T extends ViewMode> = T extends ViewMode
  ? {
      mode: T;
      id: Uuid;
      data: ViewData<T>;
      controlsAtom: PrimitiveAtom<ViewControls<T>>;
    }
  : never;

// Atom for the current content of the 3D viewport
export const viewAtom = atom<View<ViewMode> | null>(null);

// Data needed to render a Gcode file in the "Prepare" tab
export type GcodeViewData = {
  printData: GCodePrintData;
};

// Type for z range and sensor range information, used to configure controls
export type SensorPointCloudInfo = {
  zRange: Tuple<number>;
  extrudedZRange: Tuple<number>;
  sensorValueRange: Tuple<number>;
  extrudedSensorValueRange: Tuple<number>;
  fullSensorValueRange: Tuple<number>;
  fullExtrudedSensorValueRange: Tuple<number>;
  sensorHistogram: Uint32Array;
  extrudedSensorHistogram: Uint32Array;
};

// Type for an individual sensor data stream, to be toggled by the point cloud renderer
export type SensorPointCloudSource = {
  name: string;
  positions: Float32Array;
  sensorValues: Float32Array;
};

// Data needed to render print sensor data in the "Analyze" tab
export type PrintViewData = {
  startPoints: Float32Array;
  endPoints: Float32Array;
  offsets: Int32Array;
  counts: Int32Array;
  extrusionValues: Int32Array;
  segmentTools: Uint8Array;
  // Store sensor values as half-float bit patterns (`HalfFloatType`) in a
  // `Uint16Array` so the texture uses 2 bytes/texel instead of 4. WebGL seems
  // to have a hard limit of 1GB allocation for textures, and Float32 textures
  // are too large for some prints. Switching to 16-bit half-floats doubles our
  // capacity.
  //
  // Note that Uint16Array is not a typo; for half-float data WebGL2 requires a
  // 16-bit unsigned integer format even though the underlying bit pattern is a
  // Float16. Be careful using this data because its contents are not actual
  // integers, but rather the bit patterns of half-float values.
  sensorValues: Uint16Array;
  sensorTextureDimensions: Triple<number>;
  info: SensorPointCloudInfo;
};

// Data for an individual sim result stream (ie, "original" or "optimized" data)
export type SimResultSource = {
  positions: Float32Array;
  layerWelding: Float32Array;
  previousLayerTemperature: Float32Array;
};

// Data needed to render optimization data in the "Optimize" tab
export type SimViewData = {
  original: SimResultSource;
  optimized?: SimResultSource;
};

export type ViewData<T extends ViewMode> = T extends "prepare"
  ? GcodeViewData
  : T extends "analyze"
    ? PrintViewData
    : T extends "optimize"
      ? SimViewData
      : undefined;

// Control config for a Gcode file in the "Prepare" tab
export type GcodeViewControls = {
  layer: { range: Tuple<number>; value: Tuple<number> };
};

// Control config for print sensor data in the "Analyze" tab
export type PrintViewControls = {
  staleData: {
    dataUpdatedAt: number;
    statusTimestamp: string;
    onRefresh: () => void;
  } | null;
  zRange: {
    range: Tuple<number>;
    extrudedRange: Tuple<number>;
    value: Tuple<number>;
  };
  valueRange: {
    suggestedRange: Tuple<number>;
    extrudedSuggestedRange: Tuple<number>;
    fullRange: Tuple<number>;
    fullExtrudedRange: Tuple<number>;
    histogram: Uint32Array;
    extrudedHistogram: Uint32Array;
    value: Tuple<number>;
  };
  colorRange: {
    value: Tuple<number>;
  };
  lineWidth: { range: Tuple<number>; value: number };
  showTool0: boolean;
  showTool1: boolean;
  showNonExtrusionMotion: boolean;
  hideStaleDataAlert: boolean;
};

// Current values for an optimization snapshot's point cloud controls
export type SimPointCloudControlsValue = {
  valueRange: Tuple<number>;
  colorRange: Tuple<number>;
};

// Data ranges for an optimization result's streams, used to configure controls
export type SimInfo = {
  zRange: Tuple<number>;
  layerWelding: Tuple<number>;
  previousLayerTemperature: Tuple<number>;
};

// Control config for optimization data in the "Optimize" tab
export type SimViewControls = {
  source: "original" | "optimized";
  snapshot: "layerWelding" | "previousLayerTemperature";
  includesOptimized: boolean; // True if the sim data includes optimized data
  ranges: SimInfo;
  values: {
    zRange: Tuple<number>;
    layerWelding: SimPointCloudControlsValue;
    previousLayerTemperature: SimPointCloudControlsValue;
  };
};

export type ViewControls<T extends ViewMode> = T extends "prepare"
  ? GcodeViewControls
  : T extends "analyze"
    ? PrintViewControls
    : T extends "optimize"
      ? SimViewControls
      : undefined;

export type ViewerState = {
  cameraTarget: Triple<number>;
  cameraPosition: Triple<number>;
  cameraFov: number;
  cameraZoom: number;
  cameraOrtho: boolean;
  showBed: boolean;
  showStats: boolean;
};

export const INITIAL_VIEWER_STATE: ViewerState = {
  cameraTarget: [BED_DIMENSIONS.width / 2, BED_DIMENSIONS.height / 2, 0],
  cameraPosition: [650, -600, 650],
  cameraFov: 45,
  cameraZoom: 1.5,
  cameraOrtho: true,
  showStats: false,
  showBed: true,
};

// For each project, we store two viewerStates: one for the Prepare and Analyze tabs and one
// for the Optimize tab. This split is made to accommodate for the placement of the print
// in the Optimize tab, which is different from the one shared by the Prepare and Analyze tabs.
//
// If at some point this difference in placement is resolved, we can simplify the state to
// only store one viewerState per project.
type ViewerStates = {
  prepareAnalyze: ViewerState;
  optimize: ViewerState;
};

// Holds the viewer state for projects that have been viewed in the current session
type ViewerSessionState = Record<Uuid, ViewerStates>;

// Storage atom for the entire session state
// The atom is key is "separatedViewerSessionState" to reflect the separation of viewer states
// for the Prepare/Analyze and Optimize tabs. If the states are merged in the future, this key
// can be changed to "viewerSessionState".
const viewerSessionStateAtom = atomWithStorage<ViewerSessionState>(
  "separatedViewerSessionState",
  {},
  createJSONStorage(() => sessionStorage)
);

// Custom hook to get and set the viewer state for a specific project
export const useViewerState = (
  projectId: Uuid,
  viewerStateMode: "optimize" | "prepareAnalyze"
): [ViewerState, (update: Update<ViewerState>) => void] => {
  const [sessionState, setSessionState] = useAtom(viewerSessionStateAtom);

  // Return the recorded viewer state for the project ID if it exists, otherwise return the initial state
  const viewerStates = sessionState[projectId] ?? {
    prepareAnalyze: INITIAL_VIEWER_STATE,
    optimize: INITIAL_VIEWER_STATE,
  };

  const viewerState = viewerStates[viewerStateMode];

  // Function to update the viewer state for the given project ID
  // Works the same way setState does for React's `useState`
  const setViewerState = (update: Update<ViewerState>) => {
    setSessionState((prevSessionState) => {
      const isFunction = typeof update === "function";

      const prevState = prevSessionState[projectId] ?? {
        prepareAnalyze: INITIAL_VIEWER_STATE,
        optimize: INITIAL_VIEWER_STATE,
      };

      const prevViewerState = prevState[viewerStateMode];

      const updatedViewerState = isFunction ? update(prevViewerState) : update;

      const updatedViewerStates = {
        ...prevState,
        [viewerStateMode]: updatedViewerState,
      };

      return {
        ...prevSessionState,
        [projectId]: updatedViewerStates,
      };
    });
  };

  return [viewerState, setViewerState];
};

export type GLInfo = {
  maxTextureSize: number;
};

export const glInfoAtom = atom<GLInfo | null>(null);
